import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { City, Country } from "../../types/types";
import {
    activateCityAdmin,
    activateCityAdminPWA,
    createCityAdmin,
    deactivateCityAdmin,
    deactivateCityAdminPWA,
    getCitiesAdmin,
    getCountriesAdmin,
} from "../../api/administration";
import { useLanguage } from "../../context/useLanguage";
import TextInput from "../../components/ui/TextInput";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField, DropdownField } from "../../components/common/FormFields";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import Icon from "../../components/common/Icon";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";

const Cities: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [cities, setCities] = useState<City[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const { language } = useLanguage();

    useEffect(() => {
        fetchCities();
        fetchCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when UI language changes
    }, [language]);

    const fetchCities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCitiesAdmin(token, language);
            setCities(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCountriesAdmin(token, language);
            setCountries(response);
        } catch (error) {
            console.error("Failed to fetch countries", error);
        }
    };

    const filteredCities = cities
        .filter(
            (city) =>
                city.officialName?.toLowerCase().includes(search.toLowerCase()) ||
                city.countryName.toLowerCase().includes(search.toLowerCase()),
        )
        .sort((a, b) => (a.officialName && b.officialName ? a.officialName.localeCompare(b.officialName) : 0))
        .sort((a, b) => a.countryName.localeCompare(b.countryName));

    if (loading) return <div>Chargement...</div>;

    const handleToggleStatus = async (city: City, next: boolean) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }

            if (next) {
                await activateCityAdmin({ token, cityId: city.id });
            } else {
                await deactivateCityAdmin({ token, cityId: city.id });
            }

            await fetchCities();
        } catch (error) {
            console.error("Failed to update city status", error);
        }
    };

    const handleToggleStatusPWA = async (city: City, next: boolean) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }

            if (next) {
                await activateCityAdminPWA({ token, cityId: city.id });
            } else {
                await deactivateCityAdminPWA({ token, cityId: city.id });
            }

            await fetchCities();
        } catch (error) {
            console.error("Failed to update city PWA status", error);
        }
    };

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("cities")} subtitle={t("citiesSubTitle")} />
            </div>

            <section className={adminSurface.sectionFull}>
                <h2 className={adminSurface.sectionTitle}>{t("addNewCity")}</h2>
                <p className={adminSurface.sectionHelp}>
                    {t("addNewCityHelp") || "La ville sera créée inactive : activez-la ensuite dans le tableau."}
                </p>

                <Formik
                    initialValues={{ newCityName: "", newCountryId: "" }}
                    validationSchema={Yup.object({
                        newCityName: Yup.string().required(t("enterCityNameRequired")),
                        newCountryId: Yup.string().required(t("selectCountryRequired")),
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const token = localStorage.getItem("token");
                            if (!token) {
                                console.error("Token not found");
                                return;
                            }

                            await createCityAdmin({
                                token,
                                cityName: values.newCityName,
                                countryId: values.newCountryId,
                            });

                            resetForm();
                            await fetchCities();
                        } catch (error) {
                            console.error("Failed to add city", error);
                        }
                    }}
                    validateOnMount
                >
                    {({ isSubmitting, errors }) => (
                        <Form className={`${adminSurface.addForm} ${adminSurface.card}`}>
                            <div className={adminSurface.addRow}>
                                <div className={adminSurface.addField}>
                                    <label className={adminSurface.addLabel} htmlFor="newCityName">
                                        {t("enterCityNameCreate")}
                                        <span className={adminSurface.requiredAsterisk}>*</span>
                                    </label>
                                    <Field
                                        id="newCityName"
                                        name="newCityName"
                                        component={TextField}
                                        placeholder={t("enterCityNameCreate")}
                                    />
                                </div>

                                <div className={adminSurface.addField}>
                                    <label className={adminSurface.addLabel} htmlFor="newCountryId">
                                        {t("selectACountry")}
                                        <span className={adminSurface.requiredAsterisk}>*</span>
                                    </label>
                                    <Field
                                        id="newCountryId"
                                        name="newCountryId"
                                        component={DropdownField}
                                        options={countries.map((country) => ({
                                            label: country.countryName,
                                            value: country.id,
                                        }))}
                                        placeholder={t("selectACountry")}
                                        isWithoutMargin={true}
                                        fullWidth
                                    />
                                </div>

                                <button
                                    className={adminSurface.addButton}
                                    type="submit"
                                    disabled={isSubmitting || Object.keys(errors).length > 0}
                                >
                                    <Icon name="ic-plus" className="ic" size={16} />
                                    {t("add")}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchCityOrCountry")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("cityName")}</th>
                                <th>{t("country")}</th>
                                <th>App</th>
                                <th>PWA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCities.map((city) => (
                                <tr key={city.id}>
                                    <td style={{ fontWeight: 600 }}>{city.officialName}</td>
                                    <td>{city.countryName}</td>
                                    <td>
                                        <div
                                            className={`${adminSurface.adminToggle}${city.status ? ` ${adminSurface.adminToggleOn}` : ""}`}
                                        >
                                            <ToggleSwitch
                                                checked={!!city.status}
                                                onChange={(checked) => handleToggleStatus(city, checked)}
                                            />
                                            <span>{city.status ? t("active") || "On" : t("inactive") || "Off"}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div
                                            className={`${adminSurface.adminToggle}${city.statusPWA ? ` ${adminSurface.adminToggleOn}` : ""}`}
                                        >
                                            <ToggleSwitch
                                                checked={!!city.statusPWA}
                                                onChange={(checked) => handleToggleStatusPWA(city, checked)}
                                            />
                                            <span>{city.statusPWA ? t("active") || "On" : t("inactive") || "Off"}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredCities.length} {t("cities")?.toLowerCase?.() || "cities"}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default Cities;
